import { useCallback, useMemo } from 'react'

import type { Map } from 'immutable'
import { useSelector } from 'react-redux'

import type { RecipeCoreId, RecipeUUID } from '@library/menu-service'

import { getHasProspectNotSeenWizard } from 'routes/Menu/selectors/menu'
import { getBasketPostcode, getBasketRecipes } from 'selectors/basket'
import { getRecipeIdByCoreIdMap } from 'selectors/menu-recipes/getRecipeIdByCoreIdMap'

import { sumQuantities, useRecipeLimitReached } from '../limits'
import { useAddRecipe, useAddRecipeByUuid } from './useAddRecipe'
import { useRemoveRecipe, useRemoveRecipeByUuid } from './useRemoveRecipe'

/**
 * Can recipes be added to the basket at this point?
 *
 * Requires a postcode to be set
 *
 * @returns
 */
const useCanAddRecipes = () => {
  const hasProspectNotSeenWizard = useSelector(getHasProspectNotSeenWizard)
  const postcode = useSelector(getBasketPostcode)

  if (hasProspectNotSeenWizard) return true

  return Boolean(postcode)
}

/**
 * Get the recipes currently in the basket
 *
 * @returns a `Map<RecipeCoreId, number>` where the key is the recipe ID and the value is the number of that recipe in basket
 */
const useRecipeQuantities = () =>
  useSelector(getBasketRecipes) as unknown as Map<RecipeCoreId, number>

const useRecipeQuantitiesByUuid = (): Record<RecipeUUID, number> => {
  const recipeQuantities = useSelector(getBasketRecipes)
  const recipeIdByCoreIdMap = useSelector(getRecipeIdByCoreIdMap)

  return useMemo(() => {
    return Object.fromEntries(
      Object.entries(recipeQuantities.toJS()).map(([coreId, quantity]) => {
        return [recipeIdByCoreIdMap[coreId as RecipeCoreId], quantity] as [RecipeUUID, number]
      }),
    )
  }, [recipeQuantities, recipeIdByCoreIdMap])
}

/**
 * Entrypoint for the `basket recipes` information
 *
 * @returns addRecipe - add a recipe to the basket
 * @returns removeRecipe - remove a recipe from the basket
 * @returns swapRecipe - replace a recipe in the basket
 *
 */
export const useBasketRecipes = () => {
  const recipeQuantities = useRecipeQuantities()
  const recipeQuantitiesByUuid = useRecipeQuantitiesByUuid()

  const getQuantitiesForRecipeId = useCallback(
    (recipeId: RecipeUUID) => recipeQuantitiesByUuid[recipeId] || 0,
    [recipeQuantitiesByUuid],
  )

  const addRecipe = useAddRecipe()
  const addRecipeByUuid = useAddRecipeByUuid()
  const removeRecipe = useRemoveRecipe()
  const removeRecipeByUuid = useRemoveRecipeByUuid()
  const reachedLimit = useRecipeLimitReached(recipeQuantities)
  const canAddRecipes = useCanAddRecipes()

  const recipeCount = sumQuantities(recipeQuantities)

  return {
    addRecipe,
    addRecipeByUuid,
    removeRecipe,
    removeRecipeByUuid,
    canAddRecipes,
    reachedLimit,
    recipeCount,
    getQuantitiesForRecipeId,
  }
}
